import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Service, ServiceConnection } from '../../services/api/service-map.api';

interface ServiceDetailPanelProps {
  service: Service | null;
  connections: ServiceConnection[];
  allServices: Service[];
  isOpen: boolean;
  onClose: () => void;
  onServiceClick: (serviceId: string) => void;
}

const statusConfig = {
  healthy: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-500',
  },
  warning: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-500',
  },
  critical: {
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-500/30',
    dot: 'bg-red-500',
  },
  unknown: {
    bg: 'bg-gray-500/10',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-500/30',
    dot: 'bg-gray-500',
  },
};

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

// Format time ago - returns key and count for translation
const getTimeAgo = (dateStr: string): { key: string; count?: number } => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return { key: 'justNow' };
  if (diffMins < 60) return { key: 'minutesAgo', count: diffMins };
  if (diffHours < 24) return { key: 'hoursAgo', count: diffHours };
  return { key: 'daysAgo', count: diffDays };
};

export default function ServiceDetailPanel({
  service,
  connections,
  allServices,
  isOpen,
  onClose,
  onServiceClick,
}: ServiceDetailPanelProps) {
  const { t } = useTranslation('apm');

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Get connections for this service
  const { upstream, downstream } = useMemo(() => {
    if (!service) return { upstream: [], downstream: [] };

    const upstream = connections
      .filter(c => c.target_service_id === service.id)
      .map(c => ({
        ...c,
        service: allServices.find(s => s.id === c.source_service_id),
      }));

    const downstream = connections
      .filter(c => c.source_service_id === service.id)
      .map(c => ({
        ...c,
        service: allServices.find(s => s.id === c.target_service_id),
      }));

    return { upstream, downstream };
  }, [service, connections, allServices]);

  if (!service) return null;

  const status = statusConfig[service.status] || statusConfig.unknown;

  // Get real metrics from metadata (ensure they are numbers)
  const totalRequests = Number(service.metadata?.total_requests) || 0;
  const errorCount = Number(service.metadata?.error_count) || 0;
  const errorRate = Number(service.metadata?.error_rate) || 0;
  const avgLatency = Number(service.metadata?.avg_latency_ms) || 0;
  const p95Latency = Number(service.metadata?.p95_latency_ms) || 0;
  const p99Latency = Number(service.metadata?.p99_latency_ms) || 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-gray-900 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
            <div className="px-6 py-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl ${status.bg} ${status.border} border-2 flex items-center justify-center`}>
                    <div className={`w-4 h-4 rounded-full ${status.dot} animate-pulse`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {service.name}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      <span className="capitalize">{service.type}</span>
                      {service.language && <> · {service.language}</>}
                      {service.version && <> · v{service.version}</>}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-xl p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-all"
                  onClick={onClose}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Status & Environment Badges */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${status.bg} ${status.text} ${status.border} border`}>
                  <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                  {t(`serviceMap.status.${service.status}`)}
                </span>
                {service.environment && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {service.environment}
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {(() => {
                    const timeAgo = getTimeAgo(service.last_seen_at || service.updated_at);
                    return t(`serviceMap.details.timeAgo.${timeAgo.key}`, { count: timeAgo.count });
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 space-y-8">
            {/* Key Metrics */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('serviceMap.details.performanceMetrics')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Total Requests */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border border-blue-200 dark:border-blue-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{t('serviceMap.details.totalRequests')}</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {formatNumber(totalRequests)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {errorCount > 0 ? t('serviceMap.details.withErrors', { count: errorCount }) : t('serviceMap.details.noErrors')}
                  </div>
                </div>

                {/* Error Rate */}
                <div className={`p-4 rounded-xl border ${
                  errorRate > 5
                    ? 'bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10 border-red-200 dark:border-red-800/50'
                    : 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200 dark:border-emerald-800/50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-medium ${errorRate > 5 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {t('serviceMap.details.errorRate')}
                    </span>
                  </div>
                  <div className={`text-3xl font-bold ${
                    errorRate > 5 ? 'text-red-600 dark:text-red-400' :
                    errorRate > 1 ? 'text-amber-600 dark:text-amber-400' :
                    'text-emerald-600 dark:text-emerald-400'
                  }`}>
                    {errorRate.toFixed(2)}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatNumber(errorCount)} errors
                  </div>
                </div>
              </div>
            </section>

            {/* Latency Breakdown */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('serviceMap.details.latencyDistribution')}
              </h3>
              <div className="p-5 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{t('serviceMap.details.average')}</span>
                      <span className="font-bold text-gray-900 dark:text-white">{avgLatency.toFixed(1)}ms</span>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((avgLatency / Math.max(p99Latency, 100)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">P95</span>
                      <span className="font-bold text-amber-600 dark:text-amber-400">{p95Latency.toFixed(1)}ms</span>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((p95Latency / Math.max(p99Latency, 100)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">P99</span>
                      <span className="font-bold text-red-600 dark:text-red-400">{p99Latency.toFixed(1)}ms</span>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all duration-500"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Dependencies */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                {t('serviceMap.details.dependencies')}
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Upstream */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('serviceMap.details.upstream')}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        ({upstream.length})
                      </span>
                    </div>
                  </div>
                  {upstream.length > 0 ? (
                    <div className="space-y-2">
                      {upstream.map((conn) => (
                        <button
                          key={conn.id}
                          onClick={() => conn.service && onServiceClick(conn.service.id)}
                          className="w-full p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${statusConfig[conn.service?.status || 'unknown'].dot}`} />
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                              {conn.service?.name || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>{Number(conn.calls_per_minute) || 0}/min</span>
                            <span>·</span>
                            <span>{Math.round(Number(conn.avg_latency_ms) || 0)}ms</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                      {t('serviceMap.details.noUpstream')}
                    </p>
                  )}
                </div>

                {/* Downstream */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('serviceMap.details.downstream')}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        ({downstream.length})
                      </span>
                    </div>
                  </div>
                  {downstream.length > 0 ? (
                    <div className="space-y-2">
                      {downstream.map((conn) => (
                        <button
                          key={conn.id}
                          onClick={() => conn.service && onServiceClick(conn.service.id)}
                          className="w-full p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/50 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-all text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${statusConfig[conn.service?.status || 'unknown'].dot}`} />
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate group-hover:text-purple-600 dark:group-hover:text-purple-400">
                              {conn.service?.name || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>{Number(conn.calls_per_minute) || 0}/min</span>
                            <span>·</span>
                            <span>{Math.round(Number(conn.avg_latency_ms) || 0)}ms</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                      {t('serviceMap.details.noDownstream')}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Service Info */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('serviceMap.details.serviceInfo')}
              </h3>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('serviceMap.details.serviceId')}</span>
                  <span className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {service.id.slice(0, 8)}...
                  </span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('serviceMap.details.type')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{service.type}</span>
                </div>
                {service.language && (
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('serviceMap.details.language')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.language}</span>
                  </div>
                )}
                {service.version && (
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('serviceMap.details.version')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.version}</span>
                  </div>
                )}
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('serviceMap.details.lastSeen')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.last_seen_at ? new Date(service.last_seen_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
